import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client";
import { HierarchyApi, type Branch, type Constituency, type District, type Ward } from "../../admin/hierarchy/api";
import { PollingStationsApi, type PollingStation } from "../../admin/pollingStations/api";
import { ErrorPanel } from "../../shell/ErrorPanel";
import { VotersApi, type VoterCount, type VoterScopeLevel, type VoterShareEntry } from "./api";
import { PVTCapture } from "./PVTCapture";
import { PVTList } from "./PVTList";
import { VoterIntelCapture } from "./VoterIntelCapture";
import { VoterMap } from "./VoterMap";
import { VoterReports } from "./VoterReports";
import { VoterSummary } from "./VoterSummary";

/**
 * Voters: landing has Summary / Charts / Map / List tabs; /new is the count
 * capture form; /intel is the sample-size voter-intel capture.
 *
 *   /app/voters         — Summary / Charts / Map / List tabs
 *   /app/voters/new     — capture voter counts (registered + ours, with share-down)
 *   /app/voters/intel   — capture voter intel (sample-size projection)
 */
export function VotersRoutes() {
  return (
    <Routes>
      <Route index element={<VotersHome />} />
      <Route path="new" element={<VotersCapture />} />
      <Route path="intel" element={<VoterIntelCapture />} />
      <Route path="pvt/new" element={<PVTCapture />} />
    </Routes>
  );
}

type VotersTab = "summary" | "charts" | "map" | "list" | "pvt";

function VotersHome() {
  const [tab, setTab] = useState<VotersTab>("summary");
  // Period is shared across Summary / Charts / Map so the tabs are coherent.
  const [period, setPeriod] = useState<string>(String(new Date().getFullYear()));
  return (
    <div>
      <header style={{ marginBottom: 8, display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Voters</h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>
            Registered voters and our estimated supporters across Zambia.
          </p>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <label style={{ fontSize: 11, color: "var(--muted)" }}>
            Period{" "}
            <input
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder="2026"
              style={{ width: 80, marginLeft: 4 }}
            />
          </label>
          <Link to="intel" className="ghost" style={{ padding: "6px 12px" }}>+ Capture intel</Link>
          <Link to="new" className="ghost" style={{ padding: "6px 12px" }}>+ New count</Link>
        </div>
      </header>

      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid var(--border)" }}>
        <TabButton active={tab === "summary"} onClick={() => setTab("summary")}>Summary</TabButton>
        <TabButton active={tab === "charts"} onClick={() => setTab("charts")}>Charts</TabButton>
        <TabButton active={tab === "map"} onClick={() => setTab("map")}>Map</TabButton>
        <TabButton active={tab === "list"} onClick={() => setTab("list")}>List</TabButton>
        <TabButton active={tab === "pvt"} onClick={() => setTab("pvt")}>PVT</TabButton>
      </div>

      {tab === "summary" && <VoterSummary period={period} />}
      {tab === "charts" && <VoterReports period={period} />}
      {tab === "map" && <VoterMap period={period} />}
      {tab === "list" && <VotersList />}
      {tab === "pvt" && <PVTList period={period} />}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "transparent",
        border: "none",
        borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
        padding: "8px 14px",
        cursor: "pointer",
        fontWeight: active ? 600 : 400,
        color: active ? "var(--text)" : "var(--muted)",
      }}
    >
      {children}
    </button>
  );
}

// ---------- List ----------

function VotersList() {
  const counts = useQuery({ queryKey: ["voter-counts"], queryFn: () => VotersApi.list() });

  const grouped = useMemo(() => {
    const m = new Map<string, VoterCount[]>();
    for (const c of counts.data?.items ?? []) {
      const key = c.scopeLevel;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(c);
    }
    return m;
  }, [counts.data]);

  return (
    <div>
      <header style={{ marginBottom: 8, display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Voters</h1>
          <p className="muted" style={{ margin: 0, fontSize: 14 }}>
            Registered voters and our estimated supporters by location and period.
          </p>
        </div>
        <Link to="new" className="ghost" style={{ padding: "6px 12px" }}>+ New entry</Link>
      </header>

      {counts.isLoading && <p>Loading…</p>}
      {counts.error && <ErrorPanel err={counts.error} title="Couldn't load voter counts" />}

      {grouped.size === 0 && !counts.isLoading && (
        <div className="muted" style={{ padding: 16 }}>
          No voter counts yet. Click <strong>+ New entry</strong> to capture your first.
        </div>
      )}

      {Array.from(grouped.entries()).map(([level, items]) => (
        <div key={level} className="card" style={{ marginTop: 16 }}>
          <div className="card-title" style={{ textTransform: "capitalize" }}>{level} level</div>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted)" }}>
                <th style={{ padding: "4px 8px" }}>Scope ID</th>
                <th style={{ padding: "4px 8px" }}>Period</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Registered</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Ours</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>%</th>
                <th style={{ padding: "4px 8px" }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "4px 8px" }}><code>{c.scopeId}</code></td>
                  <td style={{ padding: "4px 8px" }}>{c.period}</td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>{c.registered.toLocaleString()}</td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>{c.ours.toLocaleString()}</td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                    {c.registered > 0 ? `${((c.ours / c.registered) * 100).toFixed(1)}%` : "—"}
                  </td>
                  <td style={{ padding: "4px 8px", color: "var(--muted)" }}>{c.source || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ---------- Capture ----------

interface CaptureForm {
  scopeLevel: VoterScopeLevel;
  did: string;
  cid: string;
  wid: string;
  bid: string;
  psid: string;
  period: string;
  registered: string;
  ours: string;
  sampleSize: string;
  notes: string;
  source: string;
}

const INITIAL_CAPTURE: CaptureForm = {
  scopeLevel: "district",
  did: "",
  cid: "",
  wid: "",
  bid: "",
  psid: "",
  period: String(new Date().getFullYear()),
  registered: "",
  ours: "",
  sampleSize: "",
  notes: "",
  source: "",
};

function VotersCapture() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<CaptureForm>(INITIAL_CAPTURE);
  const [error, setError] = useState<string | null>(null);
  const update = (patch: Partial<CaptureForm>) => setForm((s) => ({ ...s, ...patch }));

  // Hierarchy queries — drive the cascading pickers
  const districts = useQuery({ queryKey: ["districts"], queryFn: HierarchyApi.listDistricts });
  const constituencies = useQuery({
    queryKey: ["constituencies", form.did],
    queryFn: () => HierarchyApi.listConstituenciesInDistrict(form.did),
    enabled: !!form.did,
  });
  const wards = useQuery({
    queryKey: ["wards", form.cid],
    queryFn: () => HierarchyApi.listWards(form.cid),
    enabled: !!form.cid,
  });
  const branches = useQuery({
    queryKey: ["branches", form.wid],
    queryFn: () => HierarchyApi.listBranches(form.wid),
    enabled: !!form.wid,
  });
  const stations = useQuery({
    queryKey: ["polling-stations", form.bid],
    queryFn: () => PollingStationsApi.listForBranch(form.bid),
    enabled: !!form.bid,
  });

  // The currently-selected scope ID, derived from scopeLevel
  const scopeId = (
    form.scopeLevel === "pollingStation" ? form.psid :
    form.scopeLevel === "branch" ? form.bid :
    form.scopeLevel === "ward" ? form.wid :
    form.scopeLevel === "constituency" ? form.cid : form.did
  );

  // Auto-fill: look up existing voter counts for the chosen scope so we can
  // either load the same-period row (edit mode) or seed from the most recent
  // prior period (2021 → 2026 starting point).
  const existingForScope = useQuery({
    queryKey: ["voter-counts-by-scope", form.scopeLevel, scopeId],
    queryFn: () => VotersApi.list({ scopeLevel: form.scopeLevel, scopeId }),
    enabled: !!scopeId,
  });
  // All voter counts, used to derive Period/Source suggestions.
  const allCounts = useQuery({
    queryKey: ["voter-counts"],
    queryFn: () => VotersApi.list(),
  });
  // Distinct known periods, sorted newest first, for the Period datalist.
  const knownPeriods = useMemo(() => {
    const s = new Set<string>();
    for (const c of allCounts.data?.items ?? []) if (c.period) s.add(c.period);
    return Array.from(s).sort((a, b) => b.localeCompare(a));
  }, [allCounts.data]);
  // Most-common source already used for the currently-typed period, across all
  // scopes. Lets a fresh capture inherit "ECZ 2026 certified statement" etc.
  const suggestedSource = useMemo(() => {
    if (!form.period) return "";
    const tally = new Map<string, number>();
    for (const c of allCounts.data?.items ?? []) {
      if (c.period !== form.period || !c.source) continue;
      tally.set(c.source, (tally.get(c.source) ?? 0) + 1);
    }
    let best = "", bestN = 0;
    for (const [src, n] of tally) if (n > bestN) { best = src; bestN = n; }
    return best;
  }, [allCounts.data, form.period]);
  // Distinct sources seen anywhere — datalist suggestions in the Source input.
  const knownSources = useMemo(() => {
    const s = new Set<string>();
    for (const c of allCounts.data?.items ?? []) if (c.source) s.add(c.source);
    return Array.from(s).sort();
  }, [allCounts.data]);
  // For polling-station scope the polling-station doc itself carries the
  // ECZ-reported registered voters — useful as a fallback when no voter-count
  // row exists yet.
  const selectedStation = useMemo(
    () => (stations.data?.items ?? []).find((s) => s.id === form.psid),
    [stations.data, form.psid],
  );

  // Track which (scope, period) we already auto-filled so we don't stomp on
  // the user as they edit — re-fill only when the key actually changes.
  const prefilledFor = useRef<string>("");
  const [prefillNotice, setPrefillNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!scopeId || !form.period) return;
    const key = `${form.scopeLevel}__${scopeId}__${form.period}`;
    if (prefilledFor.current === key) return;
    if (existingForScope.isLoading) return;

    const items = existingForScope.data?.items ?? [];
    const exact = items.find((r) => r.period === form.period);
    if (exact) {
      update({
        registered: String(exact.registered),
        ours: String(exact.ours),
        sampleSize: exact.sampleSize ? String(exact.sampleSize) : "",
        notes: exact.notes || "",
        source: exact.source || "",
      });
      prefilledFor.current = key;
      setPrefillNotice(`Editing existing ${form.period} entry${exact.source ? ` (source: ${exact.source})` : ""}.`);
      return;
    }
    // No exact match — seed registered from the most recent prior period.
    const others = items.slice().sort((a, b) => b.period.localeCompare(a.period));
    if (others.length > 0) {
      const seed = others[0];
      update({
        registered: String(seed.registered),
        ours: "",
        sampleSize: "",
        // Prefer the period-wide suggested source over a generic "{year} baseline".
        source: form.source || suggestedSource || `${seed.period} baseline`,
      });
      prefilledFor.current = key;
      setPrefillNotice(
        `Registered carried over from ${seed.period} (${seed.registered.toLocaleString()}). Enter "Ours" and sample size fresh.`,
      );
      return;
    }
    // Polling-station-specific fallback: use the station's registeredVoters field.
    if (form.scopeLevel === "pollingStation" && selectedStation?.registeredVoters) {
      update({
        registered: String(selectedStation.registeredVoters),
        source: form.source || suggestedSource || "polling-station record",
      });
      prefilledFor.current = key;
      setPrefillNotice(`Registered taken from the polling-station record (${selectedStation.registeredVoters.toLocaleString()}).`);
      return;
    }
    // Nothing scope-specific to pre-fill — still inherit the period-wide source
    // suggestion so the field isn't blank when other scopes have one on file.
    if (!form.source && suggestedSource) {
      update({ source: suggestedSource });
    }
    prefilledFor.current = key;
    setPrefillNotice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, form.period, form.scopeLevel, existingForScope.data, existingForScope.isLoading, selectedStation, suggestedSource]);

  // Children always derived from the chosen scope. There is no longer a
  // share-down toggle — when a scope has children, the split table is the
  // expected next step.
  const childItems: { id: string; name: string; registered?: number }[] = useMemo(() => {
    if (form.scopeLevel === "district") {
      return (constituencies.data?.items ?? []).map((c) => ({ id: c.id, name: c.name }));
    }
    if (form.scopeLevel === "constituency") {
      return (wards.data?.items ?? []).map((w) => ({ id: w.id, name: w.name }));
    }
    if (form.scopeLevel === "ward") {
      return (branches.data?.items ?? []).map((b) => ({ id: b.id, name: b.name }));
    }
    if (form.scopeLevel === "branch") {
      return (stations.data?.items ?? []).map((ps: PollingStation) => ({ id: ps.id, name: ps.name, registered: ps.registeredVoters }));
    }
    return [];
  }, [form.scopeLevel, constituencies.data, wards.data, branches.data, stations.data]);

  // Per-child split state — keyed by child id
  const [childSplits, setChildSplits] = useState<Record<string, { registered: string; ours: string }>>({});
  // Re-initialise the split whenever the children list or parent totals change.
  // Default is an even split; the user can adjust per-row before saving.
  useEffect(() => {
    if (childItems.length === 0) { setChildSplits({}); return; }
    const totalReg = Math.max(0, Number(form.registered) || 0);
    const totalOurs = Math.max(0, Number(form.ours) || 0);
    const n = childItems.length;
    const next: Record<string, { registered: string; ours: string }> = {};
    for (const c of childItems) {
      next[c.id] = {
        registered: String(c.registered ?? Math.round(totalReg / n)),
        ours: String(Math.round(totalOurs / n)),
      };
    }
    setChildSplits(next);
  }, [form.registered, form.ours, childItems]);

  const childChildScopeLevel: VoterScopeLevel | null = (
    form.scopeLevel === "district" ? "constituency" :
    form.scopeLevel === "constituency" ? "ward" :
    form.scopeLevel === "ward" ? "branch" :
    form.scopeLevel === "branch" ? "pollingStation" : null
  );

  const create = useMutation({
    mutationFn: async () => {
      const registered = Math.max(0, Number(form.registered) || 0);
      const ours = Math.max(0, Number(form.ours) || 0);
      const sampleSize = form.sampleSize ? Math.max(0, Number(form.sampleSize) || 0) : null;
      // 1. Always persist the parent count first.
      await VotersApi.create({
        scopeLevel: form.scopeLevel,
        scopeId,
        period: form.period,
        registered,
        ours,
        sampleSize,
        notes: form.notes,
        source: form.source,
      });
      // 2. If the scope has children, persist the split rows too. The table
      // is always shown when children exist; even-split is the default and
      // the user may have adjusted per-row before save.
      if (childChildScopeLevel && childItems.length > 0) {
        const entries: VoterShareEntry[] = childItems.map((c) => ({
          scopeLevel: childChildScopeLevel,
          scopeId: c.id,
          registered: Math.max(0, Number(childSplits[c.id]?.registered) || 0),
          ours: Math.max(0, Number(childSplits[c.id]?.ours) || 0),
        }));
        if (entries.length > 0) {
          await VotersApi.shareDown({ period: form.period, entries });
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["voter-counts"] });
      nav("/app/voters");
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!scopeId) { setError("Pick a scope to record against."); return; }
    if (!form.period.trim()) { setError("Period is required (e.g. 2026)."); return; }
    if (!form.registered) { setError("Registered count is required."); return; }
    create.mutate();
  }

  const sumRegistered = childItems.reduce((s, c) => s + (Number(childSplits[c.id]?.registered) || 0), 0);
  const sumOurs = childItems.reduce((s, c) => s + (Number(childSplits[c.id]?.ours) || 0), 0);

  const isEditingExisting = !!(
    existingForScope.data?.items.some((r) => r.period === form.period)
  );

  return (
    <div>
      <Link to=".." className="muted" style={{ fontSize: 13 }}>← Back to voters</Link>
      <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 600 }}>Capture voter numbers</h1>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 14 }}>
        Record how many voters are registered and how many you believe are ours, at any level of the hierarchy.
        Optionally share the totals down to children.
      </p>

      {error && <div role="alert" className="error">{error}</div>}
      {prefillNotice && (
        <div
          className="muted"
          style={{
            marginBottom: 12, padding: "8px 12px",
            background: "var(--accent-soft, #eef4ff)",
            border: "1px solid var(--accent-border, #d6e3ff)",
            borderRadius: 6, fontSize: 12,
          }}
        >
          {prefillNotice}
        </div>
      )}

      <form onSubmit={submit} className="card" style={{ padding: 16 }}>
        <div className="row-3" style={{ gap: 8 }}>
          <Field label="Scope level">
            <select
              value={form.scopeLevel}
              onChange={(e) => update({
                scopeLevel: e.target.value as VoterScopeLevel,
                cid: "", wid: "", bid: "", psid: "",
              })}
            >
              <option value="district">District</option>
              <option value="constituency">Constituency</option>
              <option value="ward">Ward</option>
              <option value="branch">Branch</option>
              <option value="pollingStation">Polling station</option>
            </select>
          </Field>
          <Field label="Period">
            <input
              list="voter-periods"
              value={form.period}
              onChange={(e) => {
                update({ period: e.target.value });
                // Re-trigger prefill for the new period.
                prefilledFor.current = "";
              }}
              placeholder="e.g. 2026"
            />
            <datalist id="voter-periods">
              {knownPeriods.map((p) => <option key={p} value={p} />)}
            </datalist>
          </Field>
          <Field label="Source">
            <input
              list="voter-sources"
              value={form.source}
              onChange={(e) => update({ source: e.target.value })}
              placeholder={suggestedSource || "e.g. Official register 2026"}
            />
            <datalist id="voter-sources">
              {knownSources.map((s) => <option key={s} value={s} />)}
            </datalist>
          </Field>
        </div>

        <div className="row-3" style={{ gap: 8, marginTop: 8 }}>
          <Field label="District">
            <select value={form.did} onChange={(e) => update({ did: e.target.value, cid: "", wid: "", bid: "", psid: "" })}>
              <option value="">— Select —</option>
              {districts.data?.items.map((d: District) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          {form.scopeLevel !== "district" && (
            <Field label="Constituency">
              <select value={form.cid} onChange={(e) => update({ cid: e.target.value, wid: "", bid: "", psid: "" })} disabled={!form.did}>
                <option value="">— Select —</option>
                {constituencies.data?.items.map((c: Constituency) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          {(form.scopeLevel === "ward" || form.scopeLevel === "branch" || form.scopeLevel === "pollingStation") && (
            <Field label="Ward">
              <select value={form.wid} onChange={(e) => update({ wid: e.target.value, bid: "", psid: "" })} disabled={!form.cid}>
                <option value="">— Select —</option>
                {wards.data?.items.map((w: Ward) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
          )}
        </div>

        {(form.scopeLevel === "branch" || form.scopeLevel === "pollingStation") && (
          <div className="row-3" style={{ gap: 8, marginTop: 8 }}>
            <Field label="Branch">
              <select value={form.bid} onChange={(e) => update({ bid: e.target.value, psid: "" })} disabled={!form.wid}>
                <option value="">— Select —</option>
                {branches.data?.items.map((b: Branch) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            {form.scopeLevel === "pollingStation" && (
              <Field label="Polling station">
                <select value={form.psid} onChange={(e) => update({ psid: e.target.value })} disabled={!form.bid}>
                  <option value="">— Select —</option>
                  {stations.data?.items.map((ps: PollingStation) => <option key={ps.id} value={ps.id}>{ps.name}</option>)}
                </select>
              </Field>
            )}
          </div>
        )}

        <div className="row-3" style={{ gap: 8, marginTop: 12 }}>
          <Field label="Registered voters">
            <RegisteredReadout value={form.registered} hasScope={!!scopeId} />
          </Field>
          <Field label="Ours (estimated)">
            <input
              type="number" min="0"
              value={form.ours}
              onChange={(e) => update({ ours: e.target.value })}
              placeholder="0"
            />
          </Field>
          <Field label="Notes">
            <input
              value={form.notes}
              onChange={(e) => update({ notes: e.target.value })}
              placeholder="Optional"
            />
          </Field>
        </div>

        <div className="row-3" style={{ gap: 8, marginTop: 12, alignItems: "flex-start" }}>
          <Field label="Sample size">
            <input
              type="number" min="0"
              value={form.sampleSize}
              onChange={(e) => update({ sampleSize: e.target.value })}
              placeholder="e.g. 400"
            />
            <SampleConfidence
              sampleSize={Number(form.sampleSize) || 0}
              population={Number(form.registered) || 0}
            />
          </Field>
          <span />
          <span />
        </div>

        {childChildScopeLevel && (
          <div style={{ marginTop: 16, padding: 12, background: "var(--bg-muted, #f7f7f9)", borderRadius: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Per-{childChildScopeLevel} split ({childItems.length} children)
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
              Capturing at {form.scopeLevel} level distributes to {childChildScopeLevel}s automatically.
              Default is even-split — adjust each row before saving.
            </div>

            {childItems.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "var(--muted)" }}>
                      <th style={{ padding: "4px 8px" }}>{childChildScopeLevel}</th>
                      <th style={{ padding: "4px 8px", textAlign: "right" }}>Registered</th>
                      <th style={{ padding: "4px 8px", textAlign: "right" }}>Ours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {childItems.map((c) => (
                      <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }}>
                        <td style={{ padding: "4px 8px" }}>{c.name}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>
                          <input
                            type="number" min="0" style={{ maxWidth: 100, textAlign: "right" }}
                            value={childSplits[c.id]?.registered ?? "0"}
                            onChange={(e) => setChildSplits((s) => ({ ...s, [c.id]: { ...s[c.id], registered: e.target.value, ours: s[c.id]?.ours ?? "0" } }))}
                          />
                        </td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>
                          <input
                            type="number" min="0" style={{ maxWidth: 100, textAlign: "right" }}
                            value={childSplits[c.id]?.ours ?? "0"}
                            onChange={(e) => setChildSplits((s) => ({ ...s, [c.id]: { ...s[c.id], ours: e.target.value, registered: s[c.id]?.registered ?? "0" } }))}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: "1px solid var(--border)", fontWeight: 600 }}>
                      <td style={{ padding: "4px 8px" }}>Total</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>{sumRegistered.toLocaleString()}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>{sumOurs.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
                {(sumRegistered !== Number(form.registered) || sumOurs !== Number(form.ours)) && (
                  <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                    Children totals don't match the parent — that's allowed (real-world data is messy) but worth checking.
                  </div>
                )}
              </div>
            )}
            {childItems.length === 0 && (
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                No {childChildScopeLevel}s exist for this scope yet — add them in Setup before capturing here.
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : (isEditingExisting ? "Update" : "Save")}
          </button>
          <Link to=".." className="ghost" style={{ padding: "6px 12px" }}>Cancel</Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>{label}</label>
      {children}
    </div>
  );
}

// Registered voters is sourced from existing DB data (ECZ register / prior period
// / polling-station record) — it isn't a capture input. We show the number as a
// styled readout that visually echoes a disabled input.
function RegisteredReadout({ value, hasScope }: { value: string; hasScope: boolean }) {
  const n = Number(value) || 0;
  if (!hasScope) {
    return (
      <div className="muted" style={{ fontSize: 13, padding: "6px 8px", border: "1px dashed var(--border)", borderRadius: 4 }}>
        Pick a scope to see the registered number.
      </div>
    );
  }
  if (!n) {
    return (
      <div className="muted" style={{ fontSize: 13, padding: "6px 8px", border: "1px dashed var(--border)", borderRadius: 4 }}>
        Not on file — set it in Setup if known.
      </div>
    );
  }
  return (
    <div style={{
      fontSize: 14, fontWeight: 500, padding: "6px 8px",
      background: "var(--bg-muted, #f7f7f9)",
      border: "1px solid var(--border)", borderRadius: 4,
    }}>
      {n.toLocaleString()}
    </div>
  );
}

// Margin of error for a proportion at 95% confidence with worst-case p=0.5,
// finite-population corrected. Banding: <=5% good, <=10% marginal, otherwise weak.
function SampleConfidence({ sampleSize, population }: { sampleSize: number; population: number }) {
  if (!sampleSize || !population) {
    return (
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        Enter how many voters you actually asked / canvassed — we'll show whether the sample is statistically meaningful.
      </div>
    );
  }
  if (sampleSize > population) {
    return (
      <div style={{ fontSize: 11, marginTop: 4, color: "#b91c1c" }}>
        Sample ({sampleSize.toLocaleString()}) exceeds registered ({population.toLocaleString()}). Check the figures.
      </div>
    );
  }
  // Margin of error for proportion: ME = z * sqrt(p(1-p)/n) * fpc
  // z=1.96 (95%), p=0.5 (max variance), fpc = sqrt((N-n)/(N-1))
  const z = 1.96;
  const fpc = population > 1 ? Math.sqrt((population - sampleSize) / (population - 1)) : 1;
  const me = z * Math.sqrt(0.25 / sampleSize) * fpc;
  const mePct = me * 100;
  let label: string;
  let color: string;
  if (mePct <= 5) { label = "Reflective sample"; color = "#15803d"; }
  else if (mePct <= 10) { label = "Marginal sample"; color = "#b45309"; }
  else { label = "Weak sample"; color = "#b91c1c"; }
  return (
    <div style={{ fontSize: 11, marginTop: 4, color }}>
      ±{mePct.toFixed(1)}% margin at 95% confidence — <strong>{label}</strong>
      <span className="muted" style={{ marginLeft: 4, color: "var(--muted)" }}>
        (sample {sampleSize.toLocaleString()} of {population.toLocaleString()})
      </span>
    </div>
  );
}
